import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { PageHeader, Section } from "@/components/PageHeader";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import api from "@/lib/api";
import { RefreshCw, Loader2, ChevronRight, ChevronLeft, Plus, X, Server, RotateCcw, Mail, BarChart3, Check } from "lucide-react";

// Hostinger webmail: email is always support@<domain>, shared mailbox password.
const HOSTINGER_MAIL_PASSWORD = "@bcwa7IggCx5Vk^!";
const openWebmail = async (domainName, toastFn) => {
  const email = `support@${domainName}`;
  const url = `https://mail.hostinger.com/auth/login?_user=${encodeURIComponent(email)}`;
  try {
    await navigator.clipboard.writeText(HOSTINGER_MAIL_PASSWORD);
    toastFn.success("Password copied — paste into the Password field", { description: email });
  } catch {
    toastFn.message("Opening webmail", { description: `${email} · password: ${HOSTINGER_MAIL_PASSWORD}` });
  }
  window.open(url, "_blank", "noopener");
};

// Number of domains shown per page (client-side pagination).
const PER_PAGE = 20;

const PRESETS = {
  "Namecheap BasicDNS": ["dns1.registrar-servers.com", "dns2.registrar-servers.com"],
  "Cloudflare": ["", ""], // user fills personalized
  "Hostinger": ["ns1.dns-parking.com", "ns2.dns-parking.com"],
};

// Expected default Namecheap nameservers. Any NS not in this set is highlighted in red.
const DEFAULT_NS = ["dns1.registrar-servers.com", "dns2.registrar-servers.com"];
// NS whose hostname ends with one of these suffixes is considered known/OK -> shown in green.
const GREEN_NS_SUFFIXES = ["dns-parking.com", "cloudflare.com"];

const normNs = (ns) => String(ns || "").trim().toLowerCase();
const isDefaultNs = (ns) => DEFAULT_NS.includes(normNs(ns));
const isGreenNs = (ns) => {
  const n = normNs(ns);
  return GREEN_NS_SUFFIXES.some((suf) => n === suf || n.endsWith("." + suf));
};
// Returns "default" | "green" | "red" for styling.
const nsClass = (ns) => {
  if (isDefaultNs(ns)) return "text-[var(--text-secondary)]";
  if (isGreenNs(ns)) return "text-green-600 underline decoration-green-500 decoration-2 underline-offset-2";
  return "text-red-600 underline decoration-red-500 decoration-2 underline-offset-2";
};

const MyDomains = () => {
  const [domains, setDomains] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [page] = useState(1);
  // Client-side pagination: which page of the domain list is currently shown.
  const [currentPage, setCurrentPage] = useState(1);
  // Per-domain nameserver info: { [domainName]: { loading, error, nameservers: [] } }
  const [nsMap, setNsMap] = useState({});
  // True while the global "Check NS" run is in progress.
  const [nsChecking, setNsChecking] = useState(false);
  // Per-domain Google Analytics info: { [domainName]: { loading, error, has_ga, has_gtm, ids: [] } }
  const [gaMap, setGaMap] = useState({});
  // True while the global "Check GA" run is in progress.
  const [gaChecking, setGaChecking] = useState(false);
  const [renewTarget, setRenewTarget] = useState(null);
  const [renewYears, setRenewYears] = useState(1);
  const [renewBusy, setRenewBusy] = useState(false);
  // NS dialog state
  const [nsTarget, setNsTarget] = useState(null); // domain string
  const [nsList, setNsList] = useState([]);
  const [nsIsDefault, setNsIsDefault] = useState(false);
  const [nsLoading, setNsLoading] = useState(false);
  const [nsBusy, setNsBusy] = useState(false);

  const splitDomain = (name) => {
    const parts = (name || "").toLowerCase().split(".");
    if (parts.length < 2) return null;
    return { sld: parts[0], tld: parts.slice(1).join(".") };
  };

  // Fetch nameservers for a single domain and store in nsMap.
  const fetchOneNs = async (name) => {
    const sp = splitDomain(name);
    if (!sp) {
      setNsMap((cur) => ({ ...cur, [name]: { loading: false, error: true, nameservers: [] } }));
      return;
    }
    setNsMap((cur) => ({ ...cur, [name]: { loading: true, error: false, nameservers: [] } }));
    try {
      const { data } = await api.get(`/dns/${sp.sld}/${sp.tld}/nameservers`);
      const nsl = (data.nameservers || []).map((n) => String(n).trim()).filter(Boolean);
      setNsMap((cur) => ({ ...cur, [name]: { loading: false, error: false, nameservers: nsl } }));
    } catch (e) {
      setNsMap((cur) => ({ ...cur, [name]: { loading: false, error: true, nameservers: [] } }));
    }
  };

  // Fetch nameservers for every domain (limited concurrency). Triggered manually
  // via the "Check NS" button — NOT automatically, to avoid API rate limits.
  const fetchAllNs = async (list) => {
    const names = (list || []).map((d) => d.Name).filter(Boolean);
    if (!names.length) return;
    setNsChecking(true);
    setNsMap(() => {
      const init = {};
      names.forEach((n) => (init[n] = { loading: true, error: false, nameservers: [] }));
      return init;
    });

    // Simple concurrency limiter (4 at a time) to avoid hammering the API.
    const CONCURRENCY = 4;
    let idx = 0;
    const worker = async () => {
      while (idx < names.length) {
        const my = idx++;
        await fetchOneNs(names[my]);
      }
    };
    try {
      await Promise.all(Array.from({ length: Math.min(CONCURRENCY, names.length) }, worker));
    } finally {
      setNsChecking(false);
    }
  };

  // Fetch Google Analytics presence for a single domain and store in gaMap.
  const fetchOneGa = async (name) => {
    if (!name) return;
    setGaMap((cur) => ({ ...cur, [name]: { loading: true, error: false, has_ga: false, has_gtm: false, ids: [] } }));
    try {
      const { data } = await api.get(`/domains/ga-check`, { params: { domain: name } });
      setGaMap((cur) => ({
        ...cur,
        [name]: {
          loading: false,
          error: Boolean(data.error) && !data.has_ga && !data.has_gtm ? data.error : false,
          has_ga: Boolean(data.has_ga),
          has_gtm: Boolean(data.has_gtm),
          ids: data.ids || [],
        },
      }));
    } catch (e) {
      setGaMap((cur) => ({
        ...cur,
        [name]: { loading: false, error: true, has_ga: false, has_gtm: false, ids: [] },
      }));
    }
  };

  // Fetch GA presence for every domain in the list (limited concurrency).
  // Triggered manually via the "Check GA" button (fetches live websites).
  const fetchAllGa = async (list) => {
    const names = (list || []).map((d) => d.Name).filter(Boolean);
    if (!names.length) return;
    setGaChecking(true);
    setGaMap(() => {
      const init = {};
      names.forEach((n) => (init[n] = { loading: true, error: false, has_ga: false, has_gtm: false, ids: [] }));
      return init;
    });

    const CONCURRENCY = 4;
    let idx = 0;
    const worker = async () => {
      while (idx < names.length) {
        const my = idx++;
        await fetchOneGa(names[my]);
      }
    };
    try {
      await Promise.all(Array.from({ length: Math.min(CONCURRENCY, names.length) }, worker));
    } finally {
      setGaChecking(false);
    }
  };

  const openNs = async (domainName) => {
    const sp = splitDomain(domainName);
    if (!sp) return toast.error("Invalid domain");
    setNsTarget(domainName);
    setNsList([]);
    setNsIsDefault(false);
    setNsLoading(true);
    try {
      const { data } = await api.get(`/dns/${sp.sld}/${sp.tld}/nameservers`);
      const list = (data.nameservers || []).map((n) => String(n));
      setNsList(list.length ? list : ["", ""]);
      setNsIsDefault(Boolean(data.is_using_our_dns));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load nameservers");
      setNsList(["", ""]);
    } finally {
      setNsLoading(false);
    }
  };

  const saveNs = async (useDefault = false) => {
    const sp = splitDomain(nsTarget);
    if (!sp) return;
    const cleaned = nsList.map((n) => n.trim()).filter(Boolean);
    if (!useDefault && cleaned.length < 2) {
      return toast.error("Provide at least 2 nameservers");
    }
    setNsBusy(true);
    try {
      await api.post("/dns/nameservers", {
        sld: sp.sld,
        tld: sp.tld,
        nameservers: cleaned,
        use_default: useDefault,
      });
      toast.success(useDefault ? `${nsTarget} → Namecheap default DNS` : `Nameservers saved for ${nsTarget}`);
      const saved = nsTarget;
      setNsTarget(null);
      // Refresh the displayed NS for this domain (registry updates are near-instant).
      const savedNs = useDefault ? [...DEFAULT_NS] : cleaned;
      setNsMap((cur) => ({ ...cur, [saved]: { loading: false, error: false, nameservers: savedNs } }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setNsBusy(false);
    }
  };

  const fetchList = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/domains/list`, { params: { page, page_size: 100, search } });
      const sorted = (data.domains || []).slice().sort((a, b) => {
        const am = Date.parse(String(a.Created || "").replace(/-/g, "/")) || 0;
        const bm = Date.parse(String(b.Created || "").replace(/-/g, "/")) || 0;
        return bm - am; // newest first
      });
      setDomains(sorted);
      setCurrentPage(1); // reset to first page on every (re)load / search
      // NS records are NOT fetched automatically here (avoids "too many requests").
      // The user triggers NS lookups manually via the "Check NS" button.
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchList();
    // eslint-disable-next-line
  }, []);

  const doRenew = async () => {
    if (!renewTarget) return;
    setRenewBusy(true);
    try {
      await api.post("/domains/renew", { domain_name: renewTarget, years: renewYears });
      toast.success(`${renewTarget} renewed for ${renewYears}y`);
      setRenewTarget(null);
      fetchList();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Renew failed");
    } finally {
      setRenewBusy(false);
    }
  };

  // ---- client-side pagination derived values ----
  const totalCount = domains.length;
  const totalPages = Math.max(1, Math.ceil(totalCount / PER_PAGE));
  const safePage = Math.min(currentPage, totalPages);
  const startIdx = (safePage - 1) * PER_PAGE; // 0-based offset of first row on this page
  const pageItems = domains.slice(startIdx, startIdx + PER_PAGE);

  return (
    <div>
      <PageHeader
        eyebrow="Inventory"
        title="My Domains"
        description="All domains registered under this Namecheap account."
        actions={
          <div className="flex items-center gap-2">
            <Button
              data-testid="btn-check-ns"
              onClick={() => fetchAllNs(pageItems)}
              variant="outline"
              className="rounded-sm"
              disabled={nsChecking || loading || pageItems.length === 0}
              title="Fetch nameservers for the domains shown on this page"
            >
              {nsChecking ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Server className="w-4 h-4 mr-2" />
              )}
              Check NS
            </Button>
            <Button
              data-testid="btn-check-ga"
              onClick={() => fetchAllGa(pageItems)}
              variant="outline"
              className="rounded-sm"
              disabled={gaChecking || loading || pageItems.length === 0}
              title="Check Google Analytics for the domains shown on this page"
            >
              {gaChecking ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <BarChart3 className="w-4 h-4 mr-2" />
              )}
              Check GA
            </Button>
            <Button
              data-testid="btn-refresh-domains"
              onClick={fetchList}
              variant="outline"
              className="rounded-sm"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} />
              Refresh
            </Button>
          </div>
        }
      />
      <Section>
        <div className="flex items-center gap-2 mb-4">
          <Input
            data-testid="filter-input"
            placeholder="Filter by name..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && fetchList()}
            className="rounded-sm max-w-xs mono"
          />
          <Button data-testid="btn-filter" variant="outline" onClick={fetchList} className="rounded-sm">
            Apply
          </Button>
        </div>

        <div className="border border-[var(--border)] rounded-sm bg-white overflow-hidden">
          <div className="grid grid-cols-12 px-4 py-2.5 text-[11px] uppercase tracking-[0.2em] text-[var(--text-secondary)] border-b border-[var(--border)] bg-[#FAFAFA]">
            <div className="col-span-3 flex items-center gap-3">
              <span className="w-8 text-right shrink-0">#</span>
              <span>Domain</span>
            </div>
            <div className="col-span-2">Created</div>
            <div className="col-span-2">Expires</div>
            <div className="col-span-1">GA</div>
            <div className="col-span-1">AutoRenew</div>
            <div className="col-span-3 text-right">Actions</div>
          </div>

          {loading ? (
            <div className="p-4 space-y-2">
              {[1, 2, 3, 4].map((i) => (
                <Skeleton key={i} className="h-10 w-full" />
              ))}
            </div>
          ) : domains.length === 0 ? (
            <div className="p-8 text-center text-[var(--text-secondary)] text-sm">No domains found.</div>
          ) : (
            <div className="divide-y divide-[var(--border)]" data-testid="domain-list">
              {pageItems.map((d, i) => (
                <div
                  key={i}
                  className="grid grid-cols-12 items-center px-4 py-3 dense-row text-sm"
                  data-testid={`domain-row-${d.Name}`}
                >
                  <div className="col-span-3 flex items-start gap-3 min-w-0">
                    <span
                      className="w-8 shrink-0 text-right mono text-xs text-[var(--text-disabled)] pt-0.5 tabular-nums"
                      data-testid={`domain-index-${d.Name}`}
                    >
                      {startIdx + i + 1}
                    </span>
                    <div className="flex flex-col gap-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="mono">{d.Name}</span>
                      {d.IsExpired === "true" && <Badge variant="destructive" className="rounded-sm">Expired</Badge>}
                      {d.IsLocked === "true" && <Badge variant="outline" className="rounded-sm">Locked</Badge>}
                    </div>
                    <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[11px] mono" data-testid={`domain-ns-${d.Name}`}>
                      {(() => {
                        const info = nsMap[d.Name];
                        if (!info) {
                          return (
                            <button
                              type="button"
                              onClick={() => fetchOneNs(d.Name)}
                              className="text-[var(--text-disabled)] hover:text-[var(--text-primary)] flex items-center gap-1 underline decoration-dotted underline-offset-2 transition-colors"
                              data-testid={`btn-check-ns-${d.Name}`}
                              title="Fetch nameservers for this domain"
                            >
                              <Server className="w-3 h-3" /> Check NS
                            </button>
                          );
                        }
                        if (info.loading) {
                          return (
                            <span className="text-[var(--text-disabled)] flex items-center gap-1">
                              <Loader2 className="w-3 h-3 animate-spin" /> loading NS…
                            </span>
                          );
                        }
                        if (info.error) {
                          return (
                            <button
                              type="button"
                              onClick={() => fetchOneNs(d.Name)}
                              className="text-[var(--text-disabled)] hover:text-[var(--text-primary)] flex items-center gap-1 underline decoration-dotted underline-offset-2 transition-colors"
                              data-testid={`btn-recheck-ns-${d.Name}`}
                              title="Retry nameserver lookup"
                            >
                              <RotateCcw className="w-3 h-3" /> NS unavailable — retry
                            </button>
                          );
                        }
                        if (!info.nameservers.length) {
                          return <span className="text-[var(--text-disabled)]">no nameservers</span>;
                        }
                        return (
                          <>
                            <span className="text-[var(--text-secondary)] uppercase tracking-wider text-[10px]">NS:</span>
                            {info.nameservers.map((ns, ni) => (
                              <span
                                key={ni}
                                className={nsClass(ns)}
                                title={
                                  isDefaultNs(ns)
                                    ? "Namecheap default nameserver"
                                    : isGreenNs(ns)
                                    ? "Known nameserver (dns-parking / cloudflare)"
                                    : "Not a recognized nameserver"
                                }
                              >
                                {ns}
                              </span>
                            ))}
                          </>
                        );
                      })()}
                    </div>
                    </div>
                  </div>
                  <div className="col-span-2 mono text-xs text-[var(--text-secondary)]">{d.Created}</div>
                  <div className="col-span-2 mono text-xs text-[var(--text-secondary)]">{d.Expires}</div>
                  <div className="col-span-1 text-xs" data-testid={`domain-ga-${d.Name}`}>
                    {(() => {
                      const g = gaMap[d.Name];
                      if (!g) {
                        return (
                          <button
                            type="button"
                            onClick={() => fetchOneGa(d.Name)}
                            className="text-[var(--text-disabled)] hover:text-[var(--text-primary)] flex items-center gap-1 underline decoration-dotted underline-offset-2 transition-colors"
                            data-testid={`btn-check-ga-${d.Name}`}
                            title="Check Google Analytics for this domain"
                          >
                            <BarChart3 className="w-3 h-3" /> Check
                          </button>
                        );
                      }
                      if (g.loading) {
                        return (
                          <span className="text-[var(--text-disabled)] flex items-center gap-1">
                            <Loader2 className="w-3 h-3 animate-spin" />
                          </span>
                        );
                      }
                      if (g.has_ga) {
                        return (
                          <span
                            className="inline-flex items-center gap-1 text-green-700 font-medium cursor-default"
                            title={`Google Analytics found: ${(g.ids || []).join(", ")}`}
                          >
                            <Check className="w-3.5 h-3.5" /> Yes
                          </span>
                        );
                      }
                      if (g.has_gtm) {
                        return (
                          <span
                            className="inline-flex items-center gap-1 text-amber-600 cursor-default"
                            title={`Google Tag Manager found (${(g.ids || []).join(", ")}) — GA may load via GTM`}
                          >
                            <Check className="w-3.5 h-3.5" /> GTM
                          </span>
                        );
                      }
                      if (g.error) {
                        return (
                          <button
                            type="button"
                            onClick={() => fetchOneGa(d.Name)}
                            className="text-[var(--text-disabled)] hover:text-[var(--text-primary)] flex items-center gap-1 underline decoration-dotted underline-offset-2"
                            data-testid={`btn-recheck-ga-${d.Name}`}
                            title="Site unreachable — retry"
                          >
                            <RotateCcw className="w-3 h-3" /> retry
                          </button>
                        );
                      }
                      return (
                        <span
                          className="inline-flex items-center gap-1 text-red-600 cursor-default"
                          title="No Google Analytics tag found on the homepage"
                        >
                          <X className="w-3.5 h-3.5" /> No
                        </span>
                      );
                    })()}
                  </div>
                  <div className="col-span-1 text-xs mono">
                    {d.AutoRenew === "true" ? (
                      <span className="text-[var(--success)]">ON</span>
                    ) : (
                      <span className="text-[var(--text-disabled)]">OFF</span>
                    )}
                  </div>
                  <div className="col-span-3 flex justify-end items-center gap-1.5 flex-nowrap">
                    <Button
                      data-testid={`btn-email-${d.Name}`}
                      size="sm"
                      variant="outline"
                      className="rounded-sm h-8 px-2 text-xs whitespace-nowrap shrink-0"
                      onClick={() => openWebmail(d.Name, toast)}
                      title={`Open webmail for support@${d.Name}`}
                    >
                      <Mail className="w-3.5 h-3.5 mr-1" /> Check email
                    </Button>
                    <Button
                      data-testid={`btn-renew-${d.Name}`}
                      size="sm"
                      variant="outline"
                      className="rounded-sm h-8 px-2 text-xs whitespace-nowrap shrink-0"
                      onClick={() => {
                        setRenewTarget(d.Name);
                        setRenewYears(1);
                      }}
                    >
                      Renew
                    </Button>
                    <Button
                      data-testid={`btn-ns-${d.Name}`}
                      size="sm"
                      variant="ghost"
                      className="rounded-sm h-8 px-2 text-xs whitespace-nowrap shrink-0"
                      onClick={() => openNs(d.Name)}
                    >
                      <Server className="w-3.5 h-3.5 mr-1" /> NS
                    </Button>
                    <Link to={`/whois-update?domain=${d.Name}`} className="shrink-0">
                      <Button
                        data-testid={`btn-whois-update-${d.Name}`}
                        size="sm"
                        variant="ghost"
                        className="rounded-sm h-8 px-2 text-xs whitespace-nowrap"
                      >
                        WHOIS
                      </Button>
                    </Link>
                    <Link to={`/dns?domain=${d.Name}`} className="shrink-0">
                      <Button
                        data-testid={`btn-dns-${d.Name}`}
                        size="sm"
                        variant="ghost"
                        className="rounded-sm h-8 px-2 text-xs whitespace-nowrap"
                      >
                        DNS <ChevronRight className="w-3.5 h-3.5 ml-1" />
                      </Button>
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {!loading && totalCount > 0 && (
          <div
            className="flex flex-wrap items-center justify-between gap-3 mt-3 text-xs text-[var(--text-secondary)]"
            data-testid="pagination-bar"
          >
            <div className="mono" data-testid="pagination-info">
              {totalCount} {totalCount === 1 ? "domain" : "domains"} · showing{" "}
              {startIdx + 1}–{Math.min(startIdx + PER_PAGE, totalCount)}
            </div>
            <div className="flex items-center gap-2">
              <Button
                data-testid="pagination-prev"
                variant="outline"
                size="sm"
                className="rounded-sm h-8 px-3"
                disabled={safePage <= 1}
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              >
                <ChevronLeft className="w-3.5 h-3.5 mr-1" /> Prev
              </Button>
              <span className="mono px-1" data-testid="pagination-status">
                Page {safePage} / {totalPages}
              </span>
              <Button
                data-testid="pagination-next"
                variant="outline"
                size="sm"
                className="rounded-sm h-8 px-3"
                disabled={safePage >= totalPages}
                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
              >
                Next <ChevronRight className="w-3.5 h-3.5 ml-1" />
              </Button>
            </div>
          </div>
        )}
      </Section>

      <Dialog open={!!nsTarget} onOpenChange={(open) => !open && setNsTarget(null)}>
        <DialogContent className="rounded-sm sm:max-w-[560px]" data-testid="ns-dialog">
          <DialogHeader>
            <DialogTitle>
              Nameservers · <span className="mono">{nsTarget}</span>
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            {nsLoading ? (
              <div className="py-6 flex items-center gap-2 text-sm text-[var(--text-secondary)]">
                <Loader2 className="w-4 h-4 animate-spin" /> Loading current nameservers…
              </div>
            ) : (
              <>
                <div className="flex items-center justify-between">
                  <Label className="text-xs uppercase tracking-wider">Custom nameservers</Label>
                  <Badge
                    variant="outline"
                    className={`rounded-sm text-[10px] uppercase tracking-wider ${
                      nsIsDefault ? "border-green-300 text-green-700" : ""
                    }`}
                    data-testid="ns-status-badge"
                  >
                    {nsIsDefault ? "Using Namecheap DNS" : "Custom DNS"}
                  </Badge>
                </div>

                <div className="space-y-2">
                  {nsList.map((ns, i) => (
                    <div key={i} className="flex gap-2">
                      <Input
                        data-testid={`ns-input-${i}`}
                        value={ns}
                        onChange={(e) =>
                          setNsList((cur) => cur.map((n, idx) => (idx === i ? e.target.value : n)))
                        }
                        placeholder={`ns${i + 1}.example.com`}
                        className="mono rounded-sm h-9"
                      />
                      {nsList.length > 2 && (
                        <Button
                          data-testid={`ns-remove-${i}`}
                          size="sm"
                          variant="ghost"
                          className="rounded-sm h-9 w-9 p-0"
                          onClick={() => setNsList((cur) => cur.filter((_, idx) => idx !== i))}
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  ))}
                </div>

                <div className="flex flex-wrap items-center gap-2 pt-1">
                  <Button
                    data-testid="ns-add-row"
                    size="sm"
                    variant="outline"
                    className="rounded-sm h-8"
                    onClick={() => setNsList((cur) => [...cur, ""])}
                  >
                    <Plus className="w-3.5 h-3.5 mr-1" /> Add row
                  </Button>
                  <div className="text-[10px] uppercase tracking-wider text-[var(--text-secondary)] ml-auto">
                    Quick presets:
                  </div>
                  {Object.entries(PRESETS).map(([label, list]) => (
                    <Button
                      key={label}
                      data-testid={`ns-preset-${label}`}
                      size="sm"
                      variant="ghost"
                      className="rounded-sm h-8 text-xs"
                      onClick={() => setNsList(list.length ? list : ["", ""])}
                    >
                      {label}
                    </Button>
                  ))}
                </div>

                <p className="text-xs text-[var(--text-secondary)]">
                  Changes propagate at the registry within minutes; public DNS resolvers may take up to 24h.
                </p>
              </>
            )}
          </div>
          <DialogFooter className="gap-2 flex-wrap sm:justify-between">
            <Button
              data-testid="btn-ns-default"
              variant="outline"
              onClick={() => saveNs(true)}
              disabled={nsBusy || nsLoading}
              className="rounded-sm"
            >
              <RotateCcw className="w-3.5 h-3.5 mr-1.5" /> Use Namecheap default
            </Button>
            <div className="flex gap-2">
              <Button variant="ghost" onClick={() => setNsTarget(null)} className="rounded-sm">
                Cancel
              </Button>
              <Button
                data-testid="btn-ns-save"
                onClick={() => saveNs(false)}
                disabled={nsBusy || nsLoading}
                className="brand-bg hover:brand-bg text-white rounded-sm"
              >
                {nsBusy ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Save custom NS
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!renewTarget} onOpenChange={(open) => !open && setRenewTarget(null)}>
        <DialogContent className="rounded-sm">
          <DialogHeader>
            <DialogTitle>Renew <span className="mono">{renewTarget}</span></DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <Label htmlFor="years">Years</Label>
            <Input
              data-testid="renew-years"
              id="years"
              type="number"
              min={1}
              max={10}
              value={renewYears}
              onChange={(e) => setRenewYears(parseInt(e.target.value) || 1)}
              className="rounded-sm mono"
            />
            <p className="text-xs text-[var(--text-secondary)]">
              This will charge your Namecheap account for the renewal cost.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRenewTarget(null)} className="rounded-sm">
              Cancel
            </Button>
            <Button
              data-testid="btn-confirm-renew"
              onClick={doRenew}
              disabled={renewBusy}
              className="brand-bg hover:brand-bg text-white rounded-sm"
            >
              {renewBusy ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Confirm
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MyDomains;
